import React from 'react';

const METALS = [
  { id: 'cobre', label: 'Cobre' },
  { id: 'aluminio', label: 'Aluminio' },
  { id: 'zinc', label: 'Zinc' },
  { id: 'niquel', label: 'Níquel' },
  { id: 'plomo', label: 'Plomo' },
  { id: 'acero_inox', label: 'Acero inoxidable' },
  { id: 'hierro_acero', label: 'Hierro/Acero' },
  { id: 'bronce', label: 'Bronce' },
  { id: 'laton', label: 'Latón' },
];

const ToggleGroup = ({ label, desc, options, value, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
    <div>
      <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>{label}</span>
      {desc && <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>{desc}</p>}
    </div>
    <div className="flex rounded-lg border border-border overflow-hidden" style={{ background: 'var(--color-muted)' }}>
      {options?.map((opt) => (
        <button
          key={opt?.value}
          onClick={() => onChange(opt?.value)}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
            value === opt?.value ? 'text-white' : 'text-muted-foreground hover:text-foreground'
          }`}
          style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontSize: '0.8125rem',
            background: value === opt?.value ? 'var(--color-accent)' : 'transparent',
          }}
        >
          {opt?.label}
        </button>
      ))}
    </div>
  </div>
);

const PanelToggle = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
    <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>{label}</span>
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-accent' : 'bg-muted-foreground/30'
      }`}
      style={{ background: checked ? 'var(--color-accent)' : undefined }}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

export default function DashboardTab({ prefs, onChange }) {
  const set = (key) => (val) => onChange({ ...prefs, [key]: val });

  const toggleMetal = (id) => {
    const current = prefs?.dashboardMetals || METALS?.map((m) => m?.id);
    const updated = current?.includes(id) ? current?.filter((m) => m !== id) : [...current, id];
    onChange({ ...prefs, dashboardMetals: updated });
  };

  const selectedMetals = prefs?.dashboardMetals || METALS?.map((m) => m?.id);

  const panels = prefs?.panels || { exposure: true, alerts: true, margins: true, concentration: true, simulator: true };
  const setPanel = (key) => (val) => onChange({ ...prefs, panels: { ...panels, [key]: val } });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-4" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Configuración de gráficos</h3>
        <ToggleGroup
          label="Rango de tiempo predeterminado"
          desc="Para gráficos del dashboard"
          options={[{ value: '7d', label: '7d' }, { value: '30d', label: '30d' }, { value: '90d', label: '90d' }]}
          value={prefs?.defaultTimeRange || '30d'}
          onChange={set('defaultTimeRange')}
        />
        <ToggleGroup
          label="Escenario de shock predeterminado"
          desc="Mostrado en el simulador"
          options={[{ value: '-3', label: '-3%' }, { value: '-5', label: '-5%' }, { value: '-10', label: '-10%' }]}
          value={prefs?.defaultShock || '-5'}
          onChange={set('defaultShock')}
        />
        <ToggleGroup
          label="Auto-actualización de mercado"
          desc="Intervalo de refresco de datos LME"
          options={[{ value: 'off', label: 'Off' }, { value: '30', label: '30s' }, { value: '60', label: '60s' }, { value: '300', label: '5min' }]}
          value={prefs?.autoRefresh || 'off'}
          onChange={set('autoRefresh')}
        />
      </div>
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-1" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Metales en benchmark cards</h3>
        <p className="text-muted-foreground mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Selecciona los metales visibles en el dashboard</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {METALS?.map((metal) => (
            <label
              key={metal?.id}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors"
              style={{
                borderColor: selectedMetals?.includes(metal?.id) ? 'var(--color-accent)' : 'var(--color-border)',
                background: selectedMetals?.includes(metal?.id) ? 'color-mix(in srgb, var(--color-accent) 8%, transparent)' : 'var(--color-background)',
              }}
            >
              <input
                type="checkbox"
                checked={selectedMetals?.includes(metal?.id)}
                onChange={() => toggleMetal(metal?.id)}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--color-accent)' }}
              />
              <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>{metal?.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-4" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Visibilidad de paneles</h3>
        <PanelToggle label="Panel de Exposición" checked={panels?.exposure} onChange={setPanel('exposure')} />
        <PanelToggle label="Alertas de Riesgo" checked={panels?.alerts} onChange={setPanel('alerts')} />
        <PanelToggle label="Gráfico de Márgenes" checked={panels?.margins} onChange={setPanel('margins')} />
        <PanelToggle label="Gráfico de Concentración" checked={panels?.concentration} onChange={setPanel('concentration')} />
        <PanelToggle label="Simulador de Escenarios" checked={panels?.simulator} onChange={setPanel('simulator')} />
      </div>
    </div>
  );
}
